import React, { useCallback } from "react";
import MuiTextField, { StandardTextFieldProps } from "@material-ui/core/TextField";
import { makeStyles } from "@material-ui/core/styles";
import clsx from "clsx";

const useStyles = makeStyles({
  root: {
    width: "100%",
    margin: "7px 0px",
  },
  labelContainer: {
    display: "flex",
    padding: "7px 0",
    gap: "8px",
  },
  label: {
    fontSize: "14px",
    lineHeight: "21px",
    color: "#464B52",
  },
  required: {
    color: "#DA212C",
    height: "16px",
  },
  inputContainer: {
    display: "flex",
    "& div": {
      borderRadius: "4px",
    },
  },
  muiinputtext: {
    height: "48px",
    font: "inherit",
    padding: "0px 12px",
    width: "100%",
    borderRadius: "4px"
  },
  bottomBorder: {
    border: "1px solid #E0E1E9",
  },
  fullBlueBorder: {
    border: "solid 1px #171C1E",
  },
  errorFullBorder: {
    border: "1px solid #c81922",
  },
  disabled: {
    backgroundColor: '#e0e1e9',
    color: '#252A2E',
    borderBottom: `1px solid #6A6E79`
  }
});

interface InputContainerProps extends StandardTextFieldProps {
  placeholder?: string;
  label?: string | JSX.Element;
  required?: boolean;
  value?: string;
  helperText?: string;
  onChange?: (e: React.ChangeEvent<HTMLInputElement>) => void;
  name?: string;
  setError?: (error: boolean) => void;
  validate?: ValidationRules;
  disabled?: boolean;
  type?: "text"| "password";
  inputStyle?: React.CSSProperties;
  fullBorder?: boolean;
  isOnTypeValidationEnabled?: boolean;
  
}

interface ValidationRules {
  required?: boolean;
  requiredMessage?: string;
  minLength?: number;
  maxLength?: number;
  min?: number;
  max?: number;
  pattern?: string;
  allowSpace?: boolean;
  patternMessage?: string;
  /** When provided, runs before pattern check. Return non-empty string for validation error. */
  customValidator?: (value: string) => string;
}

export const InputContainer: React.FC<InputContainerProps> = ({
  placeholder,
  required,
  label,
  value,
  validate = {},
  onChange,
  disabled = false,
  name,
  inputStyle = {textAlign: "left"},
  helperText,
  type="text",
  setError,
  fullBorder = false,
  isOnTypeValidationEnabled = false,
  ...props
}) => {
  const [isError, setIsError] = React.useState("");
  const [touched, setTouched] = React.useState<boolean>( validate.required ? true : false);
  const ref = React.useRef<HTMLInputElement>(null);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const inputValue = e.target.value;
    if (touched) {
      const validationError = validateInput(inputValue);
      setIsError(validationError);
      setError && setError(validationError !== "");
    }
    onChange && onChange(e);
  };

  const handleKeyUp = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (isOnTypeValidationEnabled) {
      const validationError = validateInput(ref.current?.value);
      setIsError(validationError);
      setError && setError(validationError !== "");
    }
    onChange && onChange(event as any);
  }

  const validateInput = useCallback((inputValue: any) => {
    if (validate.required && !inputValue) {
      if(validate.requiredMessage) {
        return validate.requiredMessage ?? "This field is required";
      }
      return "This field is required";
    }

    if (!validate.allowSpace && String(inputValue).indexOf(" ") !== -1) {
      return "The input has one or more space(s). Please remove the space(s).";
    }

    if (validate.customValidator) {
      const customError = validate.customValidator(String(inputValue));
      if (customError) return customError;
    }

    if (validate.pattern && !new RegExp(validate.pattern).test(inputValue)) {
      return validate.patternMessage ?? "Invalid format";
    }

    if (validate.minLength && inputValue.length < validate.minLength) {
      return `The input must be at least ${validate.minLength} characters in length.`;
    }

    if (validate.maxLength && inputValue.length > validate.maxLength) {
      return `The input exceeds the ${validate.maxLength} character limit.`;
    }

    if (validate.min !== undefined && inputValue !== '' && Number(inputValue) < validate.min) {
      return `Value must be at least ${validate.min}`;
    }

    if (validate.max !== undefined && inputValue !== '' && Number(inputValue) > validate.max) {
      return `Value must not exceed ${validate.max}`;
    }

    return "";
  }, [validate]);


   React.useEffect(() => {
    
    if(!value || !validate || !touched || !isOnTypeValidationEnabled || !setError) {
      return;
    }

    if (touched || isOnTypeValidationEnabled) {
      const validationError = validateInput(value);
      setIsError(validationError);
      setError?.(validationError !== "");
    }
  }, [value, validate, touched, isOnTypeValidationEnabled, setIsError, setError, validateInput]);

  const handleBlur = () => {
    setTouched(true);
    const validationError = validateInput(value);
    setError && setError(validationError !== "");
    setIsError(validationError);
  };

  const classes = useStyles();

  return (
    <div style={{ fontWeight: 400 }} className={classes.root}>
      {label && (<div className={classes.labelContainer}>
        <label className={classes.label}>{label}</label>
        {required && (
          <div style={{ fontWeight: 600 }} className={classes.required}>
            *
          </div>
        )}
      </div>)}
      <MuiTextField
        className={classes.inputContainer}
        type={type}
        name={name}
        inputRef={ref}
        inputProps={{
          className: clsx([
            classes.muiinputtext,
            fullBorder ? classes.fullBlueBorder : classes.bottomBorder,
            {[classes.errorFullBorder]: (isError !== "" || helperText) && fullBorder},
            {[classes.disabled]: disabled}
          ]),
          style: inputStyle,
        }}
        placeholder={placeholder}
        value={value}
        disabled={disabled}
        helperText={isError || helperText}
        onChange={handleChange}
        onBlur={handleBlur}
        onKeyUp={handleKeyUp}
        error={isError !== "" || !!helperText}
        variant="standard"
        {...props}
      />
    </div>
  );
};